import { Stack, Typography } from "@mui/material";
import { Phone } from "react-common-ui-library";
import { PrimarySelectionCard } from "react-common-ui-library/FPACT/Cards";
import { PrimaryFormHelperText } from "react-common-ui-library/FPACT/Common";
import {
  EmailCustomIcon,
  PhoneCustomIcon,
} from "react-common-ui-library/FPACT/CustomIcons";
import { Email } from "react-common-ui-library/FPACT/Email";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import {
  CARD_EMAIL_LABEL,
  CARD_PHONE_LABEL,
  CARD_REQUIRED_ERROR,
  EMAIL_CARD_ID,
  EMAIL_ICON_ID,
  NAME_AND_CONTACT_VERIFICATION_QUESTION,
  PHONE,
  PHONE_CARD_ID,
  PREFERRED_CONTACT,
  PREFERRED_CONTACT_ERROR,
} from "../../../Constants/NameAndContactConstants";
import {
  H2,
  P_REGULAR,
  SPACING_3_2,
  SPACING_3_4,
  EMAIL,
  EMPTY,
} from "../../../Constants/CommonConstants";
import {
  SelectionCardContentBox,
  SelectionCardIconBox,
} from "../../../Styles/AccountCreation/NameAndContact/NameAndContactStyles";
import { useEffect } from "react";
import {
  PHONE_HELPER_TEXT,
  PHONE_ICON_ID,
} from "../../../Constants/LoginVerificationMethodsConstants";
import EmailLanguage from "../../Common/EmailLanguage";

/**
 * @description Contains email and phone selection cards that are used in name and contact screen.
 * @returns {ReactNode} Render the email, phone selection cards and dynamically renders email or phone input based on the user selection.
 */

const EmailOrPhoneSelectionCards = () => {
  const {
    control,
    resetField,
    formState: { errors },
  } = useFormContext();
  const intl = useIntl();

  const selectedContactMethod = useWatch({ name: PREFERRED_CONTACT });

  useEffect(() => {
    switch (selectedContactMethod) {
      case EMAIL:
        resetField(PHONE, { defaultValue: EMPTY });
        break;
      case PHONE:
        resetField(EMAIL, { defaultValue: EMPTY });
        break;
      default:
        break;
    }
  }, [resetField, selectedContactMethod]);

  return (
    <Stack spacing={SPACING_3_4}>
      <Stack spacing={SPACING_3_2}>
        <Typography
          id={NAME_AND_CONTACT_VERIFICATION_QUESTION}
          component={H2}
          variant={P_REGULAR}
          fontWeight={600}
        >
          {intl.formatMessage({
            id: NAME_AND_CONTACT_VERIFICATION_QUESTION,
          })}
        </Typography>
        <Controller
          control={control}
          name={PREFERRED_CONTACT}
          rules={{
            required: {
              message: CARD_REQUIRED_ERROR,
              value: true,
            },
          }}
          render={({ field }) => (
            <Stack spacing={SPACING_3_4}>
              <PrimarySelectionCard
                cardProps={{
                  content: (
                    <SelectionCardIconBox>
                      <EmailCustomIcon id={EMAIL_ICON_ID} />
                      <SelectionCardContentBox id={CARD_EMAIL_LABEL}>
                        {intl.formatMessage({
                          id: CARD_EMAIL_LABEL,
                        })}
                      </SelectionCardContentBox>
                    </SelectionCardIconBox>
                  ),
                  id: EMAIL_CARD_ID,
                }}
                selectionValue={EMAIL}
                selected={field.value === EMAIL}
                onChange={field.onChange}
                ref={field.ref}
              />
              <PrimarySelectionCard
                cardProps={{
                  content: (
                    <SelectionCardIconBox>
                      <PhoneCustomIcon id={PHONE_ICON_ID} />
                      <SelectionCardContentBox id={CARD_PHONE_LABEL}>
                        {intl.formatMessage({
                          id: CARD_PHONE_LABEL,
                        })}
                      </SelectionCardContentBox>
                    </SelectionCardIconBox>
                  ),
                  id: PHONE_CARD_ID,
                }}
                selectionValue={PHONE}
                selected={field.value === PHONE}
                onChange={field.onChange}
                ref={field.ref}
              />
            </Stack>
          )}
        />
        {errors && errors?.[PREFERRED_CONTACT] ? (
          <PrimaryFormHelperText
            error
            helperTextId={PREFERRED_CONTACT_ERROR}
            helperText={intl.formatMessage({
              id: errors?.[PREFERRED_CONTACT]?.message,
            })}
          />
        ) : null}
      </Stack>
      {selectedContactMethod === EMAIL ? (
        <>
          <Email />
          <EmailLanguage />
        </>
      ) : null}
      {selectedContactMethod === PHONE ? (
        <Phone helperText={PHONE_HELPER_TEXT} />
      ) : null}
    </Stack>
  );
};

export default EmailOrPhoneSelectionCards;
